import type { Variable } from "../types";

// Re-export the Variable type for convenience
export type { Variable };

// Constantes para los tipos de firma
export const SignatureTypeEnum = {
  /**
   * A simple signature, typically involving an authorization check
   * and user registration, role, or email.
   */
  SIMPLE: "simple",

  /**
   * A certified signature that requires an additional authentication
   * factor, such as a PIN or a unique key.
   */
  CERTIFIED: "certified",

  /**
   * An institutional seal applied to the document,
   * representing official institutional authority.
   */
  automatic: "automatic",
} as const;

export type SignatureTypeValue =
  (typeof SignatureTypeEnum)[keyof typeof SignatureTypeEnum];

// Tipos para la firma digital
export interface SignatureType {
  id: string;
  name: string;
  description: string;
  icon: string; // Nuevo campo para el icono
}

export interface FileToSign {
  id: string;
  name: string;
  source: string; // variable name or file path
}

export interface Signer {
  id: string;
  name: string;
  email: string;
  role: string;
  avatar?: string;
  positionId: string; // Nuevo campo para asociar con posición
  pivot?: {
    created_at: string;
    position: number;
    sign_request_id: string;
    signed_at: null;
    signer_ip: null;
    type: "required" | "optional";
    updated_at: string;
    user_id: string;
  };
}

export interface WorkPosition {
  id: string;
  name: string;
  users: Signer[];
  userCount: number;
}

export interface DigitalSignatureData {
  title: string;
  description?: string;
  signatureTypeId: string;
  signatureTypeName: string;
  filesToSign: FileToSign[];
  signers: SignersSelectedType;
}

export interface DigitalSignatureDataReceived {
  title: string;
  description?: string;
  signatureTypeId: string;
  signatureTypeName: string;
  filesToSign: FileToSign[];
  signers: Array<{
    primary: string | null;
    backup: string | null;
  }>;
}
// Datos hardcodeados para desarrollo
export const signatureTypes: SignatureType[] = [
  {
    id: "simple",
    name: "Firma simple",
    description: "Firma electrónica básica",
    icon: "pen-field",
  },
  {
    id: "certified",
    name: "Firma certificada",
    description: "Firma electrónica certificada con validación",
    icon: "signature",
  },
  {
    id: "automatic",
    name: "Sello de institución",
    description: "Sello oficial de la institución",
    icon: "stamp",
  },
];

export const mockFilesToSign: FileToSign[] = [
  {
    id: "file1",
    name: "documento_generado.pdf",
    source: "documento_variable",
  },
  {
    id: "file2",
    name: "contrato.pdf",
    source: "contrato_variable",
  },
];

export const mockSigners: Signer[] = [
  {
    id: "signer1",
    name: "Juan Pérez",
    email: "juan.perez@example.com",
    role: "Gerente General",
    positionId: "director",
  },
  {
    id: "signer2",
    name: "María García",
    email: "maria.garcia@example.com",
    role: "Directora Ejecutiva",
    positionId: "director",
  },
  {
    id: "signer3",
    name: "Carlos López",
    email: "carlos.lopez@example.com",
    role: "Supervisor de Auditoría",
    positionId: "auditor-jefe",
  },
  {
    id: "signer4",
    name: "Ana Martínez",
    email: "ana.martinez@example.com",
    role: "Coordinadora Financiera",
    positionId: "coordinador",
  },
  {
    id: "signer5",
    name: "Luis Rodríguez",
    email: "luis.rodriguez@example.com",
    role: "Analista Senior",
    positionId: "coordinador",
  },
  {
    id: "signer6",
    name: "Carmen Fernández",
    email: "carmen.fernandez@example.com",
    role: "Jefe de Operaciones",
    positionId: "jefe-operaciones",
  },
  {
    id: "signer7",
    name: "Roberto Silva",
    email: "roberto.silva@example.com",
    role: "Especialista Legal",
    positionId: "especialista",
  },
  {
    id: "signer8",
    name: "Elena Morales",
    email: "elena.morales@example.com",
    role: "Técnico de Sistemas",
    positionId: "tecnico",
  },
  {
    id: "signer9",
    name: "Diego Herrera",
    email: "diego.herrera@example.com",
    role: "Consultor Externo",
    positionId: "consultor",
  },
  {
    id: "signer10",
    name: "Patricia Jiménez",
    email: "patricia.jimenez@example.com",
    role: "Administradora de Sistemas",
    positionId: "coordinador",
  },
];

// Mock data para posiciones de trabajo (mantenemos esto como mock)
export const mockWorkPositions: WorkPosition[] = [
  {
    id: "director",
    name: "Director",
    users: [], // Vacío porque ahora se obtienen del API
    userCount: 0, // Se calculará dinámicamente
  },
  {
    id: "auditor-jefe",
    name: "Auditor Jefe",
    users: [], // Vacío porque ahora se obtienen del API
    userCount: 0, // Se calculará dinámicamente
  },
  {
    id: "coordinador",
    name: "Coordinador",
    users: [], // Vacío porque ahora se obtienen del API
    userCount: 0, // Se calculará dinámicamente
  },
  {
    id: "jefe-operaciones",
    name: "Jefe de Operaciones",
    users: [], // Vacío porque ahora se obtienen del API
    userCount: 0, // Se calculará dinámicamente
  },
  {
    id: "especialista",
    name: "Especialista",
    users: [], // Vacío porque ahora se obtienen del API
    userCount: 0, // Se calculará dinámicamente
  },
  {
    id: "tecnico",
    name: "Técnico",
    users: [], // Vacío porque ahora se obtienen del API
    userCount: 0, // Se calculará dinámicamente
  },
  {
    id: "consultor",
    name: "Consultor",
    users: [], // Vacío porque ahora se obtienen del API
    userCount: 0, // Se calculará dinámicamente
  },
];

export type SignersSelectedType = Array<{
  primary: Signer | null;
  backup: Signer | null;
}>;

export type SignerType = "primary" | "backup";
